import { useContext, useEffect, useState, useTransition } from 'react';
import { useQuery } from '@tanstack/react-query';
import { generalGet } from '../../../API/api';
import TableSkeleton from '../../../components/loaders/TableSkeleton';
import ListHeader from '../../../components/ListHeader';
import { handleSorting, hasPermission } from '../../../utils/HelperFunctions';
import { useTranslation } from 'react-i18next';
import { authContext } from '../../../store/context/authContext';
import { sortStyles } from '../../../utils/SelectStyles';
import Select, { GroupBase, OptionsOrGroups } from 'react-select';
import { TOptions } from 'i18next';
import { IDistributor } from '../../../modules/distributors/types/interfaces';
import DistributorTableContainer from '../../../modules/distributors/components/DistributorTableContainer';
import useModuleTableHelpers from '../../../hooks/useModuleTableHelpers';

const Distributors = () => {
  const { catchError } = useContext(authContext);
  const { t, i18n } = useTranslation();

  const [selectedOption, setSelectedOption] = useState(null);
  const [, startTransition] = useTransition();
  const [refetch, setRefetch] = useState(false);
  const [shownList, setShownList] = useState<IDistributor[]>([]);
  const [distributors, setDistributors] = useState<IDistributor[]>([]);

  const { currentRoute, setMetaData, searchEle, paginationEle } = useModuleTableHelpers({
    route: 'distributors',
    withBreadcrumb: true,
    withSearch: true,
    breadcrumbTitle: t('links.distributors'),
    withPagination: true
  });
  const { data, isSuccess, isLoading, error } = useQuery({
    queryKey: ['distributors', refetch, i18n, currentRoute],
    queryFn: () => generalGet(currentRoute),
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    error && catchError(error);
  }, [error]);

  useEffect(() => {
    const reqData = data?.data.data;
    isSuccess && setDistributors(reqData?.map((item: IDistributor) => ({ ...item, distributorName: item.name[i18n.language as keyof typeof item.name] })));
    setMetaData(data?.data?.meta);
  }, [isSuccess, data]);

  const tableHeaders = [
    { label: t('name'), customClass: 'logo' },
    { label: t('address') },
    { label: t('phone') },
    { label: t('status'), customClass: 'status_col' },
    hasPermission(['distributors.edit']) && { label: t('actions'), customClass: 'actions_col' }
  ];

  useEffect(() => {
    setSelectedOption(null);
    setShownList(distributors);
  }, [distributors]);

  const options: OptionsOrGroups<TOptions, GroupBase<TOptions>> = [
    {
      label: t('sort_by_status'),
      value: 'is_active'
    },
    {
      label: t('created_at'),
      value: 'id'
    }
  ];

  const applySorting = (e: any) => {
    setSelectedOption(e);
    handleSorting({ setState: setShownList, list: shownList, value: e.value, startTransition });
  };

  if (isLoading) return <TableSkeleton columns={6} withoutButton />;

  return (
    <div className="distributors-page-container">
      <ListHeader>
        <div className="filter_container">
          {searchEle}
          <Select
            value={selectedOption}
            placeholder={t('sort_by')}
            isSearchable={false}
            isClearable={false}
            isMulti={false}
            options={options}
            className={`select-drop-down `}
            noOptionsMessage={() => t('no_options')}
            classNamePrefix="react-select"
            styles={sortStyles as any}
            onChange={applySorting}
          />
        </div>
      </ListHeader>
      <DistributorTableContainer tableHeaders={tableHeaders} data={shownList} noDataMessage={t('no_distributor_found')} setRefetchData={setRefetch} lessColumns />
      {paginationEle}
    </div>
  );
};

export default Distributors;
